import { Component, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import {animate, style, transition, trigger} from '@angular/animations';
import {NavigationEnd, NavigationStart, Router, RouterOutlet} from '@angular/router';
import {ApplicationService} from '../generic/service/application.service';
import {Message} from '../generic/classes/message';
import {ConfigurationHelper} from '../generic/helper/configuration.helper';
import {CookiesDisabledTranslationsComponent} from '../generic/cookies.disabled.translations.component';
import {WindowRef} from '../generic/service/window-ref.service';
import {CookieService} from '../generic/service/cookie.service';
import {CookieBannerComponent} from '../generic/component/cookies/cookie.banner.component';
import {NavbarComponent} from './navbar/navbar.component';
import {FooterComponent} from './footer/footer.component';
import {CookieBannerModalComponent} from '../generic/component/cookies/cookie.banner.modal.component';

export enum KeyCode {
	CTRL_LEFT = 'ControlLeft',
	CTRL_RIGHT = 'ControlRight',
	CMD_LEFT = 'MetaLeft',
	CMD_RIGHT = 'MetaRight',
}

@Component({
	selector: 'noviflora-webshop',
	animations: [
		trigger('fadeInOutAnimation', [
			transition(':enter', [
				style({opacity: 0}),
				animate('0.5s ease-out', style({opacity: 1, zIndex: 100})),
			]),
			transition(':leave', [
				style({opacity: 1}),
				animate('0.5s ease-in', style({opacity: 0, zIndex: 100})),
			]),
		]),
	],
	template: `
		<cookies-disabled-translations></cookies-disabled-translations>

		<navigation-bar></navigation-bar>

		<div class="body-content">
			<router-outlet></router-outlet>
		</div>

		<footer-menu></footer-menu>

		@if (!hasMadeCookieChoice()) {
			<cookie-banner [@fadeInOutAnimation]></cookie-banner>
		}

		@if (applicationService.isCustomizeCookiePopup()) {
			<cookie-banner-modal></cookie-banner-modal>
		}
	`,
	standalone: true,
	imports: [
		CookiesDisabledTranslationsComponent,
		CookieBannerComponent,
		NavbarComponent,
		FooterComponent,
		CookieBannerModalComponent,
		RouterOutlet
	],
})
export class AppComponent implements OnInit {
	protected applicationService = inject(ApplicationService);
	private cookieService = inject(CookieService);
	private router = inject(Router);

	private static winRef: WindowRef = new WindowRef();

	@ViewChild(CookiesDisabledTranslationsComponent, {static: true})
	cookiesDisabledTranslationComponent: CookiesDisabledTranslationsComponent;

	ngOnInit() {
		if (!ConfigurationHelper.isCookiesAvailable()) {
			this.showMessage(
				new Message(
					this.cookiesDisabledTranslationComponent.getCookiesDisabled(),
					'error',
					true,
				),
			);
		}

		this.router.events.subscribe(evt => {
			if (evt instanceof NavigationStart) {
				const windowObj = AppComponent.winRef.nativeWindow;

				if (typeof windowObj._gaq !== 'undefined' && windowObj._gaq) {
					windowObj._gaq.push(['_trackPageview', evt.url]);
				}
				document
					.querySelector('html')!
					.classList.remove('mobile-menu-active', 'filter-open');
			}

			if (
				evt instanceof NavigationStart ||
				evt instanceof NavigationEnd
			) {
				window.scrollTo(0, 0);
			}
		});
	}

	private showMessage(message: Message) {
		this.applicationService.message = message;
	}

	@HostListener('window:keydown', ['$event'])
	triggerCommandOrControlKeyDown(event: KeyboardEvent) {
		const windowObj = AppComponent.winRef.nativeWindow;

		if (
			event.code === KeyCode.CTRL_LEFT ||
			event.code === KeyCode.CTRL_RIGHT ||
			event.code === KeyCode.CMD_LEFT ||
			event.code === KeyCode.CMD_RIGHT
		) {
			windowObj.openInNewTab = true;
			return;
		}

		windowObj.openInNewTab = false;
	}

	@HostListener('window:keyup', ['$event'])
	triggerCommandOrControlKeyUp(_event: KeyboardEvent) {
		const windowObj = AppComponent.winRef.nativeWindow;
		windowObj.openInNewTab = false;
	}

	hasMadeCookieChoice(): boolean {
		return this.hasAcceptedCookies() || this.hasHiddenCookieBanner();
	}

	hasAcceptedCookies(): boolean {
		return this.cookieService.getCookieByName(
			CookieBannerComponent.ACCEPTED_COOKIES,
		);
	}

	hasHiddenCookieBanner(): boolean {
		return this.cookieService.getCookieByName(
			CookieBannerComponent.HIDE_COOKIE_BANNER_NAME,
		);
	}
}
